import { Overlay } from "@angular/cdk/overlay";
import { inject, Injectable, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import {
  catchError,
  filter,
  finalize,
  forkJoin,
  map,
  switchMap,
  tap,
  throwError,
} from "rxjs";
import { ConfirmComponent } from "src/app/shared/components/confirm-generic/confirm.component";
import { withOverlayOnlyScrollStrategy } from "src/app/shared/components/confirm-generic/confirm-dialog.options";
import {
  describeApiRequestSucceeded,
  describeModalConfirmed,
  describeModalOpened,
  extractSupportErrorMessage,
  recordApiRequestFailed,
  recordApiRequestStarted,
} from "src/app/shared/helpers/contact-support-history.helper";
import {
  eventAction,
  ViewContextHistoryStateService,
} from "src/app/shared/services/view-context-history-state.service";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import { BillingService } from "../billing.service";
import { BillingServiceOverviewStore } from "./billing-service-overview.store";
import { UsageOverviewDialogService } from "./usage-overview-dialog.service";

@Injectable({
  providedIn: "root",
})
export class UsageOverviewReverseCancellationService {
  private readonly dialog = inject(MatDialog);
  private readonly overlay = inject(Overlay);
  private readonly translate = inject(TranslateService);
  private readonly billingService = inject(BillingService);
  private readonly overviewStore = inject(BillingServiceOverviewStore);
  private readonly usageOverviewDialog = inject(UsageOverviewDialogService);
  private readonly loaderSvc = inject(LoaderService);
  private readonly toast = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly viewContextHistorySvc = inject(ViewContextHistoryStateService);

  readonly reversing = signal(false);

  keepService(event?: Event): void {
    event?.stopPropagation();

    if (this.reversing() || this.overviewStore.isBusy()) {
      return;
    }

    this.recordSupportHistory("modal_opened", describeModalOpened("reverse subscription cancellation"));

    this.dialog
      .open(
        ConfirmComponent,
        withOverlayOnlyScrollStrategy(this.overlay, {
          data: {
            title: this.translate.instant("modals.reverse_cancellation.title"),
            message: this.translate.instant("modals.reverse_cancellation.message"),
            okAction: this.translate.instant(
              "modals.reverse_cancellation.confirmButton",
            ),
          },
        }),
      )
      .afterClosed()
      .pipe(
        tap((confirmed) => {
          if (!confirmed) {
            return;
          }

          this.recordSupportHistory(
            "modal_confirmed",
            describeModalConfirmed("reverse subscription cancellation"),
          );
        }),
        filter(Boolean),
        switchMap(() => {
          recordApiRequestStarted(
            (action, description) => this.recordSupportHistory(action, description),
            "reverse subscription cancellation",
          );
          this.reversing.set(true);
          this.loaderSvc.show();
          return this.billingService.reverseSubscriptionCancellation().pipe(
            switchMap(() => this.refreshOverviewAndBillingSubscriptionIfNeeded()),
            catchError((error) => {
              recordApiRequestFailed(
                (action, description, options) =>
                  this.recordSupportHistory(action, description, options?.error),
                "reverse subscription cancellation",
                error,
                "mutation",
              );
              return throwError(() => error);
            }),
            finalize(() => {
              this.reversing.set(false);
              this.loaderSvc.hide();
            }),
          );
        }),
      )
      .subscribe({
        next: () => {
          this.recordSupportHistory(
            "action_succeeded",
            describeApiRequestSucceeded("reverse subscription cancellation"),
          );
          this.toast.success(
            this.translate.instant("messages.reverseSubscriptionCancellationSuccess"),
          );
        },
      });
  }

  private recordSupportHistory(
    eventAction: eventAction,
    eventDescription: string,
    error?: unknown,
  ): void {
    this.viewContextHistorySvc.createContext({
      titleView: "Usage Overview",
      eventAction,
      eventDescription,
      thisState: {
        overview: this.overviewStore.overview(),
        errorMessage: error ? extractSupportErrorMessage(error) : null,
      },
      route: this.resolveRouteSnapshot(),
      setAutomaticallyScreenshot: false,
      error: error ? extractSupportErrorMessage(error) : undefined,
    });
  }

  private resolveRouteSnapshot() {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot;
  }

  /**
   * Usage widgets and Usage Overview use GET /billing/service-overview only.
   * GET /billing/subscription runs only when a billing screen is active (Deactivate button).
   */
  private refreshOverviewAndBillingSubscriptionIfNeeded() {
    const overviewRefresh$ =
      this.usageOverviewDialog.refreshServiceOverviewIfNeeded();

    if (!this.isBillingSubscriptionUiActive()) {
      return overviewRefresh$;
    }

    return forkJoin({
      overview: overviewRefresh$,
      subscription: this.billingService.refreshSubscriptionAndNotifyListeners(),
    }).pipe(map(({ overview }) => overview));
  }

  private isBillingSubscriptionUiActive(): boolean {
    const path = this.router.url.split("?")[0].split("#")[0];
    return (
      path === "/billing" ||
      path.startsWith("/initial-setup/billing") ||
      path.startsWith("/activate-service/billing")
    );
  }
}
